import { createSignal, JSX, Show, onMount } from "solid-js";
import { useTelescopeContext } from "@hubble/telescope";
import { goBack } from "~/shared_states/modal";
import { clientRepo } from "~/server/apis/client_repo";
import { getClientIdFromCookie } from "~/utils/common";
import { isIOS } from "~/utils/platform";
import { COINS_CREDITED_POPUP_KEY } from "~/data/consts";

export type RedeemCodeModalTelescopeProps = {
  userName: string;
  onSuccess: (amount: number) => void;
  refreshV4HomePage: () => Promise<void>;
};

export function RedeemCodeModalTelescope(
  props: RedeemCodeModalTelescopeProps
): JSX.Element {
  const { components } = useTelescopeContext();
  const [code, setCode] = createSignal("");
  const [error, setError] = createSignal<string | null>(null);
  const [isRedeeming, setIsRedeeming] = createSignal(false);

  let inputRef: HTMLInputElement | undefined;

  onMount(() => {
    if (!isIOS()) {
      // do not focus on input on iOS, because ios does not support auto focus on input
      setTimeout(() => inputRef?.focus(), 100);
    }
  });

  const handleRedeem = async () => {
    const cleanCode = code().replaceAll("-", "");

    if (!cleanCode || cleanCode.length === 0) {
      setError("Please enter a code");
      return;
    }

    setIsRedeeming(true);
    setError(null);

    try {
      const clientId = getClientIdFromCookie();
      const response = await clientRepo.applyGiftCard(cleanCode, clientId);

      if (response.transactionStatus === "SUCCESS") {
        await props.refreshV4HomePage();
        goBack(() => props.onSuccess(response.amount));
        sessionStorage.setItem(COINS_CREDITED_POPUP_KEY, "true");
      }
    } catch (err: any) {
      setError(err.message ?? "Something went wrong");
    } finally {
      setIsRedeeming(false);
    }
  };

  const formattedCode = () => {
    return code()
      .replaceAll("-", "")
      .replace(/(.{4})/g, "$1-")
      .replace(/-$/, "");
  };

  const handleSubmit = (e: Event) => {
    e.preventDefault();
    if (!isRedeeming() && !error()) {
      handleRedeem();
    }
  };

  return (
    <div class="relative flex flex-col overflow-hidden bg-background-normal-primary">
      <components.ModalHeader
        size="large"
        title="Redeem code"
        description="Add coins to your account"
        // icon={() => (
        //   <img src={GiftIconUrl} alt="gift" class="size-full object-cover" />
        // )}
        onClose={() => {
          if (!isRedeeming()) goBack();
        }}
      />

      <form onSubmit={handleSubmit} class="flex w-full flex-1 flex-col">
        <div class="box-border flex w-full flex-col gap-4 px-5 pb-5 pt-0">
          <components.Input
            ref={inputRef}
            value={formattedCode()}
            onChange={(value) => {
              setCode(value);
              setError(null);
            }}
            placeholder="Enter code"
            size={40}
            state={error() ? "error" : isRedeeming() ? "disabled" : "default"}
            helperText={error() || undefined}
          />
        </div>

        <div class="mt-auto">
          <components.ModalFooter
            button={{
              text: "Redeem",
              onClick: handleRedeem,
              variant: "primary",
              style: "neutral",
              size: 40,
              loading: isRedeeming(),
              disabled: isRedeeming() || !!error(),
            }}
          />
        </div>
      </form>
    </div>
  );
}

const GiftIconUrl =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32' fill='none'%3E%3Cpath fill='%235c5c5c' d='M28 12H4C3.44772 12 3 12.4477 3 13V15C3 15.5523 3.44772 16 4 16H28C28.5523 16 29 15.5523 29 15V13C29 12.4477 28.5523 12 28 12Z'/%3E%3Cpath fill='%235c5c5c' d='M4 18C3.44772 18 3 18.4477 3 19V27C3 28.1046 3.89543 29 5 29H27C28.1046 29 29 28.1046 29 27V19C29 18.4477 28.5523 18 28 18H17V27H15V18H4Z'/%3E%3Cpath fill='%235c5c5c' d='M16 3C14.3431 3 13 4.34315 13 6C13 6.35064 13.0602 6.68722 13.1707 7H10C8.89543 7 8 7.89543 8 9C8 10.1046 8.89543 11 10 11H22C23.1046 11 24 10.1046 24 9C24 7.89543 23.1046 7 22 7H18.8293C18.9398 6.68722 19 6.35064 19 6C19 4.34315 17.6569 3 16 3Z'/%3E%3C/svg%3E";
